<template>
  <div class="add-quotation-item-modal-body">
    <v-toolbar class="modal-toolbar" density="comfortable" flat>
      <div class="modal-bar">
        <v-btn icon variant="text" class="back-btn" aria-label="Back" @click="modal.close()">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div class="modal-title">{{ props.mode === "edit" ? "Edit item" : "Add item" }}</div>
      </div>
    </v-toolbar>

    <v-card-text class="body py-6">
      <v-form class="form" @submit.prevent="handleSubmit">
        <AppTextField v-model="name" label="Item name" placeholder="e.g. Termite treatment" />
        <AppTextField v-model="qty" type="number" label="Qty" placeholder="1" />
        <AppTextField v-model="unit" label="Unit" placeholder="e.g. pcs, sqm" />
        <AppTextField v-model="rate" type="number" label="Rate" placeholder="0.00" />

        <div class="actions">
          <AppButtonFlat block class="mb-4" type="submit" prepend-icon="mdi-check" color="primary"
            :loading="isSubmitting" :disabled="isSubmitting || !isValid || isDeleting">
            {{ props.mode === "edit" ? "Save" : "Add" }}
          </AppButtonFlat>

          <AppButtonFlat v-if="props.mode === 'edit'" block type="button" prepend-icon="mdi-delete-outline"
            color="error" variant="outlined" :loading="isDeleting" :disabled="isDeleting || isSubmitting"
            @click="handleDelete">
            Delete
          </AppButtonFlat>
        </div>
      </v-form>
    </v-card-text>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useModalStore } from "@/shared/stores/modal";
import { useSnackbarStore } from "@/shared/stores/snackbar";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { saveTemplateSectionItems } from "@/modules/vendor/services/quotation-templates.api";
import { deleteTemplateSectionItem } from "@/modules/vendor/services/quotation-templates.api";

export type QuotationItemInput = {
  title: string;
  qty: string | number | "";
  unit: string;
  rate: string | number;
};

const props = defineProps<{
  sectionId: string | number;
  itemId?: string | number;
  mode?: "add" | "edit";
  onAdd?: (item: QuotationItemInput) => void;
  onSave?: (item: QuotationItemInput) => void;
  onDeleted?: () => void | Promise<void>;
  initial?: Partial<QuotationItemInput>;
}>();

const modal = useModalStore();
const snackbar = useSnackbarStore();
const isSubmitting = ref(false);
const isDeleting = ref(false);

const name = ref(props.initial?.title ?? "");
const qty = ref<string | number | "">(props.initial?.qty ?? "");
const unit = ref(props.initial?.unit ?? "");
const rate = ref<string | number>(props.initial?.rate ?? 0);

const isValid = computed(() => {
  const n = name.value.trim();
  return Boolean(n);
});

function handleSubmit() {
  if (!isValid.value) return;
  if (isSubmitting.value) return;

  isSubmitting.value = true;

  const payload: QuotationItemInput = {
    title: name.value.trim(),
    qty: qty.value === "" ? "" : qty.value,
    unit: unit.value.trim(),
    rate: rate.value,
  };

  saveTemplateSectionItems(props.sectionId, {
    id: props.mode === "edit" ? props.itemId ?? null : null,
    title: payload.title,
    qty: payload.qty === "" ? null : payload.qty,
    description: null,
    unit: payload.unit || null,
    unit_price: payload.rate ?? null,
    seq_no: null,
  })
    .then(async () => {
      if (props.mode === "edit") {
        await Promise.resolve(props.onSave?.(payload));
      } else {
        await Promise.resolve(props.onAdd?.(payload));
      }

      modal.close();
    })
    .catch((error) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Unable to save item.";
      snackbar.error(message);
    })
    .finally(() => {
      isSubmitting.value = false;
    });
}

function handleDelete() {
  if (isSubmitting.value || isDeleting.value) return;
  if (!props.itemId) return;

  isDeleting.value = true;

  deleteTemplateSectionItem(props.itemId)
    .then(async () => {
      return Promise.resolve(props.onDeleted?.());
    })
    .then(() => {
      modal.close();
    })
    .catch((error) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Unable to delete item.";
      snackbar.error(message);
    })
    .finally(() => {
      isDeleting.value = false;
    });
}
</script>

<style scoped>
.add-quotation-item-modal-body {
  padding: 0 0 calc(env(safe-area-inset-bottom, 0px) + 16px);
  min-height: 100dvh;
}

.modal-toolbar {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--color-theme);
  padding: calc(env(safe-area-inset-top, 0px) + 8px) 0 0;
  height: auto;
}

.modal-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0;
}

:deep(.modal-toolbar .v-toolbar__content) {
  height: auto;
  min-height: 64px;
  align-items: center;
  padding: 8px 16px;
}

.back-btn {
  width: 50px;
  height: 50px;
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.body {
  padding-top: 12px;
}

.form {
  display: grid;
  gap: 12px;
}

.actions {
  margin-top: 6px;
}
</style>
